import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BsArrowLeft, BsChevronDown } from "react-icons/bs";
import { DropDownComp, ConfirmationModal } from "../../../components";
import FallbackImage from "../../../components/Common/ImageWrapper";
import CopyComp from "../../../components/Common/CopyComp";
import ProfileSkeletonLoader from "../../../components/Common/ProfileSkeletonLoader";
import {
  useGetCurrentStaffProfileQuery,
  useSwitchBranchMutation,
  useAssignBranchMutation,
} from "../../../store/admin/staff";
import { toast } from "react-hot-toast";
import { formatDate } from "../../../utils/dateFormat";

const fontMain = { fontFamily: "'Work Sans', sans-serif" };
const fontHeading = { fontFamily: "'DM Sans', sans-serif" };

const getStringValue = (value) => {
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null) {
    return value.name || value.title || JSON.stringify(value);
  }
  return value?.toString() || "—";
};

const normalizeBranches = (branches) => {
  if (!branches) return [];
  if (Array.isArray(branches)) return branches;
  if (typeof branches === "object" && branches !== null) return [branches];
  return [];
};

const SectionHeading = ({ children }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="h-5 w-1 bg-[var(--color-primary,#8b313a)] rounded-full" />
    <h5
      className="font-bold text-[#1E1E1E] text-sm uppercase tracking-widest mb-0"
      style={fontHeading}
    >
      {children}
    </h5>
  </div>
);

const InfoField = ({ label, value, copyable = false }) => (
  <div>
    <p
      className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1.5"
      style={fontHeading}
    >
      {label}
    </p>
    <div className="flex items-center gap-2 group">
      <p className="text-[#1E1E1E] font-semibold text-sm mb-0 break-words">
        {value}
      </p>
      {copyable && value && value !== "—" ? (
        <CopyComp
          text={value}
          iconClassName="opacity-40 group-hover:opacity-100 transition-opacity"
        />
      ) : null}
    </div>
  </div>
);

const StatusBadge = ({ isSuspended }) => {
  const active = isSuspended === 0;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
        active
          ? "bg-green-500/15 text-green-700 border border-green-500/20"
          : "bg-red-500/15 text-red-700 border border-red-500/20"
      }`}
      style={fontHeading}
    >
      {active ? "Active" : isSuspended === 1 ? "Suspended" : "Unknown"}
    </span>
  );
};

const StaffProfile = () => {
  const navigate = useNavigate();
  const userData = useSelector((state) => state.auth.userData);

  const {
    data: profileData,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetCurrentStaffProfileQuery(undefined, {
    refetchOnMountOrArgChange: 30,
  });

  const [switchBranch, { isLoading: isSwitchingBranch }] =
    useSwitchBranchMutation();
  const [assignBranch, { isLoading: isRemovingBranch }] =
    useAssignBranchMutation();

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    branchToDelete: null,
  });
  const [switchBranchModal, setSwitchBranchModal] = useState({
    isOpen: false,
    branchToSwitch: null,
  });
  const [isBranchDropdownOpen, setIsBranchDropdownOpen] = useState(false);

  const currentData = profileData || userData;
  const allBranches = normalizeBranches(currentData?.branch);
  const activeBranchId = currentData?.current_branch?.id;

  const handleSwitchBranchClick = (branch) => {
    setIsBranchDropdownOpen(false);
    setSwitchBranchModal({ isOpen: true, branchToSwitch: branch });
  };

  const handleBranchSwitch = async (branchId) => {
    const toastId = toast.loading("Switching branch...");
    try {
      await switchBranch(branchId).unwrap();
      toast.success("Branch switched successfully", { id: toastId });
    } catch (error) {
      toast.error(
        error?.data?.message || "Failed to switch branch",
        { id: toastId }
      );
    }
  };

  const handleConfirmSwitchBranch = async () => {
    if (!switchBranchModal.branchToSwitch) return;
    await handleBranchSwitch(switchBranchModal.branchToSwitch.id);
    setSwitchBranchModal({ isOpen: false, branchToSwitch: null });
  };

  const handleDeleteBranchClick = (branch) => {
    if (branch.id === activeBranchId) {
      toast.error(
        "Cannot remove the active branch. Switch to another branch first."
      );
      return;
    }
    setConfirmModal({ isOpen: true, branchToDelete: branch });
  };

  const handleConfirmDeleteBranch = async () => {
    if (!confirmModal.branchToDelete) return;

    try {
      const toastId = toast.loading("Removing branch...");
      const remainingBranches = allBranches
        .filter((branch) => branch.id !== confirmModal.branchToDelete.id)
        .map((branch) => ({ branch_id: branch.id }));

      const response = await assignBranch({
        payload: { branches: remainingBranches },
        staffId: currentData?.staff_id,
      });

      if (response.error) {
        toast.error(response.error.data?.message || "Failed to remove branch", {
          id: toastId,
        });
      } else {
        toast.success("Branch removed successfully", { id: toastId });
        refetch();
      }
    } catch {
      toast.error("An error occurred while removing branch");
    } finally {
      setConfirmModal({ isOpen: false, branchToDelete: null });
    }
  };

  const branchOptions = allBranches.map((branch) => {
    const isActive = activeBranchId === branch.id;
    return {
      name: (
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center gap-2">
            <span
              className={`rounded-circle d-inline-block ${
                isActive ? "bg-success" : "bg-secondary"
              }`}
              style={{ width: 8, height: 8 }}
            />
            <span className="fw-medium text-dark">{branch.name}</span>
          </div>
          {isActive ? (
            <span className="badge bg-success-subtle text-success border-0">
              Active
            </span>
          ) : null}
        </div>
      ),
      action: () => handleSwitchBranchClick(branch),
      disabled: isActive || isSwitchingBranch,
    };
  });

  if (isLoading) {
    return (
      <section className="mx-2 mx-lg-4 pb-5" style={fontMain}>
        <ProfileSkeletonLoader />
      </section>
    );
  }

  if (error) {
    const errorMessage =
      error?.data?.message ||
      error?.error ||
      "Failed to load profile data. Please try again.";
    return (
      <section className="mx-2 mx-lg-4 pb-5" style={fontMain}>
        <ProfileHeader onBack={() => navigate(-1)} />
        <div className="bg-white rounded-[20px] border border-red-100 p-6 mt-4">
          <h5 className="text-red-700 fw-bold mb-2">Error loading profile</h5>
          <p className="text-muted mb-4">{errorMessage}</p>
          <button
            type="button"
            className="btn btn-primary px-4 py-2 rounded-3"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? "Retrying…" : "Try again"}
          </button>
        </div>
      </section>
    );
  }

  const roleName = getStringValue(currentData?.role?.name);
  const companyName = getStringValue(currentData?.company?.name);
  const hasLocation =
    currentData?.address ||
    currentData?.city ||
    currentData?.country;

  return (
    <section className="mx-2 mx-lg-4 pb-5" style={fontMain}>
      <ProfileHeader onBack={() => navigate(-1)} />

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 mt-2">
        {/* Left — identity & branch switch */}
        <div className="xl:col-span-4 flex flex-col gap-5">
          <div className="bg-white rounded-[20px] shadow-sm border border-gray-50 overflow-hidden">
            <div className="h-24 bg-gradient-to-r from-[var(--color-primary,#8b313a)] to-[color-mix(in_srgb,var(--color-primary,#8b313a),black_18%)]" />
            <div className="px-6 pb-6 -mt-12 text-center">
              <div
                className="mx-auto mb-3 rounded-full overflow-hidden border-4 border-white shadow-lg bg-white"
                style={{ width: 96, height: 96 }}
              >
                <FallbackImage
                  src={
                    currentData?.photo ||
                    currentData?.profile_image ||
                    currentData?.avatar ||
                    "/Staff/avatar.svg"
                  }
                  width="100%"
                  height="100%"
                  style={{ objectFit: "cover", display: "block" }}
                />
              </div>
              <h4
                className="fw-bold text-[#1E1E1E] mb-1 text-xl"
                style={fontHeading}
              >
                {currentData?.name || "Staff Member"}
              </h4>
              <p className="text-muted text-sm mb-3">{roleName}</p>
              <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
                <StatusBadge isSuspended={currentData?.is_suspended} />
                {currentData?.current_branch?.name ? (
                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-[#eaf1f4] text-[#31778c] border border-[#31778c]/15"
                    style={fontHeading}
                  >
                    {currentData.current_branch.name}
                  </span>
                ) : null}
              </div>
              <p className="text-xs text-muted mb-0">
                Staff ID · {getStringValue(currentData?.staff_id)}
              </p>
            </div>
          </div>

          {allBranches.length > 1 ? (
            <div className="bg-white rounded-[20px] shadow-sm border border-gray-50 p-5">
              <SectionHeading>Active branch</SectionHeading>
              <DropDownComp
                dropdownShow={isBranchDropdownOpen}
                onDropdownToggle={setIsBranchDropdownOpen}
                dropLabelClassName={`border px-3 py-2.5 rounded-xl w-100 ${
                  isSwitchingBranch
                    ? "border-warning bg-light opacity-75"
                    : "border-gray-200 bg-white"
                }`}
                arrayComp={branchOptions}
                ItemClassName="px-3 py-2 border-bottom"
                input="name"
                DropDownText={
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <span className="fw-medium text-dark text-sm">
                      {isSwitchingBranch
                        ? "Switching…"
                        : currentData?.current_branch?.name || "Select branch"}
                    </span>
                    {!isSwitchingBranch ? (
                      <BsChevronDown size={16} className="text-muted" />
                    ) : (
                      <span
                        className="spinner-border spinner-border-sm text-primary"
                        role="status"
                      />
                    )}
                  </div>
                }
              />
              <p className="text-xs text-muted mt-3 mb-0">
                Switching updates your active workspace and branch-scoped data.
              </p>
            </div>
          ) : null}
        </div>

        {/* Right — compact grouped details */}
        <div className="xl:col-span-8 flex flex-col gap-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-white rounded-[20px] shadow-sm border border-gray-50 p-6">
              <SectionHeading>Contact</SectionHeading>
              <div className="grid grid-cols-1 gap-5">
                <InfoField
                  label="Email"
                  value={getStringValue(currentData?.email)}
                  copyable
                />
                <InfoField
                  label="Phone"
                  value={getStringValue(currentData?.phone)}
                  copyable
                />
              </div>
            </div>

            <div className="bg-white rounded-[20px] shadow-sm border border-gray-50 p-6">
              <SectionHeading>Work & account</SectionHeading>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <InfoField label="Role" value={roleName} />
                <InfoField label="Company" value={companyName} />
                <InfoField
                  label="Joined"
                  value={formatDate(currentData?.created_at)}
                />
                <InfoField
                  label="Status"
                  value={getStringValue(
                    currentData?.status ||
                      (currentData?.is_suspended ? "Suspended" : "Active")
                  )}
                />
              </div>
            </div>
          </div>

          {hasLocation ? (
            <div className="bg-white rounded-[20px] shadow-sm border border-gray-50 p-6">
              <SectionHeading>Location</SectionHeading>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <InfoField
                  label="Address"
                  value={getStringValue(currentData?.address)}
                />
                <InfoField
                  label="City"
                  value={getStringValue(currentData?.city)}
                />
                <InfoField
                  label="Country"
                  value={getStringValue(currentData?.country)}
                />
              </div>
            </div>
          ) : null}

          {allBranches.length > 0 ? (
            <div className="bg-white rounded-[20px] shadow-sm border border-gray-50 overflow-hidden">
              <div className="px-6 pt-6 pb-4 flex flex-wrap items-center justify-between gap-3">
                <SectionHeading>
                  Assigned branches ({allBranches.length})
                </SectionHeading>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm mb-0">
                  <thead>
                    <tr className="bg-[#383838] text-white">
                      <th className="text-start fw-semibold py-3 px-6 text-xs uppercase tracking-wider">
                        Branch
                      </th>
                      <th className="text-start fw-semibold py-3 px-4 text-xs uppercase tracking-wider d-none d-md-table-cell">
                        Contact
                      </th>
                      <th className="text-start fw-semibold py-3 px-4 text-xs uppercase tracking-wider">
                        Status
                      </th>
                      <th className="text-end fw-semibold py-3 px-6 text-xs uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allBranches.map((branch) => {
                      const isActive = activeBranchId === branch.id;
                      const contact = [branch.phone, branch.email]
                        .filter(Boolean)
                        .join(" · ");

                      return (
                        <tr
                          key={branch.id}
                          className="border-bottom border-gray-100 align-middle"
                        >
                          <td className="py-3 px-6">
                            <div className="fw-semibold text-[#1E1E1E]">
                              {branch.name}
                            </div>
                            {branch.address ? (
                              <div className="text-muted text-xs mt-0.5 d-md-none">
                                {branch.address}
                              </div>
                            ) : null}
                          </td>
                          <td className="py-3 px-4 text-muted d-none d-md-table-cell">
                            <div className="text-xs">
                              {contact || "—"}
                            </div>
                            {branch.address ? (
                              <div className="text-xs mt-0.5 truncate max-w-[220px]">
                                {branch.address}
                              </div>
                            ) : null}
                          </td>
                          <td className="py-3 px-4">
                            {isActive ? (
                              <span className="badge rounded-pill bg-success px-3">
                                Active
                              </span>
                            ) : (
                              <span className="badge rounded-pill bg-light text-muted border px-3">
                                Assigned
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-6 text-end">
                            <div className="d-inline-flex align-items-center gap-2">
                              {!isActive && allBranches.length > 1 ? (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary rounded-pill px-3"
                                    onClick={() =>
                                      handleSwitchBranchClick(branch)
                                    }
                                    disabled={isSwitchingBranch}
                                  >
                                    Switch
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger rounded-pill px-2"
                                    onClick={() =>
                                      handleDeleteBranchClick(branch)
                                    }
                                    title="Remove branch"
                                    aria-label={`Remove ${branch.name}`}
                                  >
                                    <i className="fas fa-trash" style={{ fontSize: 11 }} />
                                  </button>
                                </>
                              ) : (
                                <span className="text-muted text-xs">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, branchToDelete: null })}
        onConfirm={handleConfirmDeleteBranch}
        title="Remove Branch"
        message={`Remove "${confirmModal.branchToDelete?.name}" from your assigned branches? This cannot be undone.`}
        confirmText="Yes, remove"
        cancelText="Cancel"
        confirmButtonClass="bg-danger text-white"
        cancelButtonClass="bg-secondary text-white"
        isLoading={isRemovingBranch}
      />

      <ConfirmationModal
        isOpen={switchBranchModal.isOpen}
        onClose={() =>
          setSwitchBranchModal({ isOpen: false, branchToSwitch: null })
        }
        onConfirm={handleConfirmSwitchBranch}
        title="Switch active branch"
        message={`Switch to "${switchBranchModal.branchToSwitch?.name}"? Your active workspace will change.`}
        confirmText="Yes, switch"
        cancelText="Cancel"
        confirmButtonClass="bg-primary text-white"
        cancelButtonClass="bg-secondary text-white"
        isLoading={isSwitchingBranch}
      />
    </section>
  );
};

function ProfileHeader({ onBack }) {
  return (
    <div className="w-full h-[72px] flex items-center px-4 md:px-6 bg-white rounded-[20px] shadow-sm border border-gray-50">
      <BsArrowLeft
        size={22}
        onClick={onBack}
        className="me-3 pointer hover:text-[var(--color-primary,#8b313a)] transition-colors flex-shrink-0"
        role="button"
        aria-label="Go back"
      />
      <h4
        className="text-xl font-bold text-[#1E1E1E] mb-0"
        style={fontHeading}
      >
        My profile
      </h4>
    </div>
  );
}

export default StaffProfile;
